"use strict";

/*
 * Bandenmeesters core storefront runtime.
 * Owns only primitives used by the canonical USED storefront:
 * filters, quantity/cart updates, mini-cart drawer and focus management.
 */

function getFocusableElements(container) {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

const trapFocusHandlers = {};

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);
  if (elementToFocus?.focus) elementToFocus.focus();
}

function trapFocus(container, elementToFocus = container) {
  if (!container) return;
  const elements = getFocusableElements(container);
  const first = elements[0] || container;
  const last = elements[elements.length - 1] || first;

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };
  trapFocusHandlers.focusout = () => document.removeEventListener("keydown", trapFocusHandlers.keydown);
  trapFocusHandlers.keydown = (event) => {
    if (event.code?.toUpperCase() !== "TAB") return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first?.focus?.();
    } else if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last?.focus?.();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);
  (elementToFocus?.focus ? elementToFocus : first)?.focus?.();
}

function debounce(func, wait, immediate) {
  let timeout;
  return function executedFunction(...args) {
    const context = this;
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: `application/${type}` },
  };
}

function getUniqueSectionNames(sectionsToRender) {
  return [...new Set((sectionsToRender || []).map((section) => section.section).filter(Boolean))];
}

function isSectionRenderTargetPresent(target) {
  if (!target?.id) return false;
  const element = document.getElementById(target.id);
  if (!element) return false;
  if (!target.selector) return true;
  return element.matches(target.selector) || Boolean(element.querySelector(target.selector));
}

const isTouchDevice = () =>
  "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;

if (isTouchDevice()) {
  document.querySelectorAll(".navigation-main .has-sub-menu > a").forEach((mainMenuNav) => {
    mainMenuNav.addEventListener("click", (event) => {
      if (mainMenuNav.classList.contains("is-dropdown-open")) return;
      document.querySelectorAll(".navigation-main .has-sub-menu > a.is-dropdown-open").forEach((item) => {
        if (item !== mainMenuNav) item.classList.remove("is-dropdown-open");
      });
      event.preventDefault();
      mainMenuNav.classList.add("is-dropdown-open");
    });
  });
}

const sidebarDrawerButton = document.querySelector(".button-sidebar-drawer-open");
const sidebarDrawer = document.querySelector(".page-layout-sidebar");

if (sidebarDrawerButton && sidebarDrawer) {
  const closeSidebar = () => {
    document.body.classList.remove("page-layout-sidebar-drawer-open", "overflow-hidden-tablet");
    removeTrapFocus(sidebarDrawerButton);
  };

  document.body.addEventListener("click", (event) => {
    const target = event.target;
    if (target.closest?.(".js-button-sidebar-drawer-dismiss")) {
      closeSidebar();
      return;
    }
    if (target.closest?.(".button-sidebar-drawer-open")) {
      document.body.classList.add("page-layout-sidebar-drawer-open", "overflow-hidden-tablet");
      trapFocus(sidebarDrawer, sidebarDrawer.querySelector(".page-layout-sidebar-drawer-header") || sidebarDrawer);
      return;
    }
    if (
      document.body.classList.contains("page-layout-sidebar-drawer-open") &&
      !target.closest?.(".page-layout-sidebar")
    ) {
      closeSidebar();
    }
  });

  sidebarDrawer.addEventListener("keyup", (event) => {
    if (event.code?.toUpperCase() === "ESCAPE") closeSidebar();
  });
}

document.addEventListener("change", (event) => {
  const wrapper = event.target.parentNode;
  if (!wrapper?.classList?.contains("select-custom")) return;
  const label = wrapper.querySelector("label");
  if (label && event.target.options) label.textContent = event.target.options[event.target.selectedIndex]?.text || "";
});

class CollapsibleExpandable extends HTMLElement {
  connectedCallback() {
    this.collapsed = this.getAttribute("expanded") === "false";
    this.toggleElement = this.querySelector(".facet-toggle");
    this.list = this.querySelector(".facet-options-list");
    this.listCollapsed = this.list?.getAttribute("aria-expanded") === "false";
    this.expandListToggle = this.querySelector(".facet-button-more");
    this.shouldCollapseOthers = this.dataset.collapseOthers === "true";
    this.onToggleBound = this.onToggle.bind(this);
    this.onListToggleBound = this.onListToggle.bind(this);
    this.toggleElement?.addEventListener("click", this.onToggleBound);
    this.expandListToggle?.addEventListener("click", this.onListToggleBound);
  }

  disconnectedCallback() {
    this.toggleElement?.removeEventListener("click", this.onToggleBound);
    this.expandListToggle?.removeEventListener("click", this.onListToggleBound);
  }

  onToggle(event) {
    event.preventDefault();
    this.collapsed ? this.expand() : this.collapse();
  }

  collapse() {
    this.setAttribute("expanded", "false");
    this.toggleElement?.setAttribute("aria-expanded", "false");
    this.collapsed = true;
  }

  expand() {
    this.setAttribute("expanded", "true");
    this.toggleElement?.setAttribute("aria-expanded", "true");
    this.collapsed = false;
    if (this.shouldCollapseOthers && this.parentElement) {
      for (const child of this.parentElement.children) {
        if (child !== this && child.tagName === "COLLAPSIBLE-EXPANDABLE") child.collapse?.();
      }
    }
  }

  onListToggle(event) {
    event.preventDefault();
    this.listCollapsed ? this.expandList() : this.collapseList();
  }

  collapseList() {
    this.list?.setAttribute("aria-expanded", "false");
    if (this.expandListToggle) this.expandListToggle.innerHTML = "&plus; " + window.productsStrings.facetsShowMore;
    this.listCollapsed = true;
  }

  expandList() {
    this.list?.setAttribute("aria-expanded", "true");
    if (this.expandListToggle) this.expandListToggle.innerHTML = "&minus; " + window.productsStrings.facetsShowLess;
    this.listCollapsed = false;
  }
}
if (!customElements.get("collapsible-expandable")) customElements.define("collapsible-expandable", CollapsibleExpandable);

class ExpandableList extends HTMLElement {
  constructor() {
    super();
    this.expandableNavs = this.querySelectorAll(".has-sub-menu");
    this.expandableToggles = this.querySelectorAll(".navigation-side-toggle");
    this.expandOnArrowsOnly = this.getAttribute("expand-on-arrows") === "true";
    if (!this.expandOnArrowsOnly) {
      this.expandableNavs.forEach((nav) => nav.querySelector("a")?.addEventListener("click", this.onToggle.bind(this, nav)));
    }
    this.expandableToggles.forEach((toggle) => {
      const nav = toggle.closest(".navigation-side-item");
      if (nav) toggle.addEventListener("click", this.onToggle.bind(this, nav));
    });
  }

  onToggle(nav, event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const submenu = nav.querySelector("ul");
    if (!submenu) return;
    const expanded = submenu.getAttribute("aria-expanded") === "true";
    expanded ? this.contract(nav) : this.expand(nav);
    if (!expanded && this.hasAttribute("close-others")) {
      nav.closest("ul")?.querySelectorAll(":scope > .has-sub-menu").forEach((item) => {
        if (item !== nav) this.contract(item);
      });
    }
  }

  expand(element) {
    const submenu = element.querySelector("ul");
    const toggle = element.querySelector(".navigation-side-toggle");
    if (!submenu) return;
    element.classList.add("is-expanded");
    submenu.setAttribute("aria-expanded", "true");
    toggle?.setAttribute("aria-expanded", "true");
    const label = toggle?.querySelector("span.visually-hidden");
    if (label && toggle.dataset.collapseText) label.textContent = toggle.dataset.collapseText;
  }

  contract(element) {
    const submenu = element.querySelector("ul");
    const toggle = element.querySelector(".navigation-side-toggle");
    if (!submenu) return;
    element.classList.remove("is-expanded");
    submenu.setAttribute("aria-expanded", "false");
    toggle?.setAttribute("aria-expanded", "false");
    const label = toggle?.querySelector("span.visually-hidden");
    if (label && toggle.dataset.expandText) label.textContent = toggle.dataset.expandText;
  }
}
if (!customElements.get("expandable-list")) customElements.define("expandable-list", ExpandableList);

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.onButtonClickBound = this.onButtonClick.bind(this);
    this.querySelectorAll("button").forEach((button) => button.addEventListener("click", this.onButtonClickBound));
    this.input?.addEventListener("change", () => this.validateQtyRules());
  }

  connectedCallback() {
    this.validateQtyRules();
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.validateQtyRules());
    }
  }

  disconnectedCallback() {
    this.quantityUpdateUnsubscriber?.();
  }

  onButtonClick(event) {
    event.preventDefault();
    if (!this.input) return;
    const previousValue = this.input.value;
    if (event.currentTarget.name === "plus") this.input.stepUp();
    else this.input.stepDown();
    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
      document.dispatchEvent(new CustomEvent("product:quantity-update", { detail: { quantity: Number(this.input.value) } }));
    }
  }

  validateQtyRules() {
    if (!this.input) return;
    const value = Number(this.input.value);
    const minus = this.querySelector("button[name='minus']");
    const plus = this.querySelector("button[name='plus']");
    if (minus && this.input.min !== "") minus.classList.toggle("disabled", value <= Number(this.input.min));
    if (plus && this.input.max !== "") plus.classList.toggle("disabled", value >= Number(this.input.max));
  }
}
if (!customElements.get("quantity-input")) customElements.define("quantity-input", QuantityInput);

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      event.preventDefault();
      const owner = this.closest("cart-items") || this.closest("mini-cart");
      owner?.updateQuantity?.(this.dataset.index, 0);
    });
  }
}
if (!customElements.get("cart-remove-button")) customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.eventContext = this.dataset.eventContext;
    this.lineItemStatusElement = document.getElementById("shopping-cart-line-item-status");
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
      (total, input) => total + (parseInt(input.value, 10) || 0),
      0,
    );
    this.addEventListener("change", debounce((event) => this.onChange(event), 500));
  }

  onChange(event) {
    if (!event.target.matches?.('[name="updates[]"]')) return;
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement?.getAttribute?.("name") || "");
  }

  getSectionsToRender() {
    return [
      { id: "header", section: document.getElementById("header")?.dataset.id, selector: ".head-slot-cart-link" },
      { id: "cart-page-title-wrap", section: document.getElementById("cart-page-title-wrap")?.dataset.id, selector: ".page-title-wrap" },
      { id: "main-cart-items", section: document.getElementById("main-cart-items")?.dataset.id, selector: ".cart-items" },
      { id: "main-cart-footer", section: document.getElementById("main-cart-footer")?.dataset.id, selector: ".cart-footer" },
      { id: "cart-live-region-text", section: "cart-live-region-text", selector: ".shopify-section" },
    ].filter(isSectionRenderTargetPresent);
  }

  updateQuantity(line, quantity, name = "") {
    this.enableLoading(line);
    const preChangeRow = document.getElementById(`CartItem-${line}`) || document.getElementById(`MiniCartItem-${line}`);
    const changedVariantId = parseInt(preChangeRow?.dataset.variantId, 10) || null;
    const sections = getUniqueSectionNames(this.getSectionsToRender());

    fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({ line, quantity, sections, sections_url: window.location.pathname }),
    })
      .then((response) => response.text())
      .then(async (state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`quantity-input-${line}`) || document.getElementById(`Mini-Cart-Quantity-${line}`);
        const items = document.querySelectorAll(".js-cart-item");

        if (parsedState.errors) {
          await this.updateSectionContents();
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        document.getElementById("main-cart-footer")?.classList.toggle("is-empty", parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const root = document.getElementById(section.id);
          if (!root) return;
          let containers = root.querySelectorAll(section.selector);
          if (!containers.length) containers = [root];
          const replacement = this.getSectionInnerHTML(parsedState.sections?.[section.section], section.selector);
          if (replacement === null) return;
          containers.forEach((element) => { if (element) element.innerHTML = replacement; });
        });

        const updatedValue = parsedState.items?.[line - 1]?.quantity;
        let message = "";
        if (quantityElement && items.length === parsedState.items?.length && updatedValue !== parseInt(quantityElement.value, 10)) {
          message = typeof updatedValue === "undefined"
            ? window.cartStrings.error
            : window.cartStrings.quantityError.replace("[quantity]", updatedValue);
        }
        this.updateLiveRegions(line, message);

        if (name) {
          const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`MiniCartItem-${line}`);
          lineItem?.querySelector(`[name="${CSS.escape(name)}"]`)?.focus?.();
        }

        document.dispatchEvent(
          new CustomEvent(parseInt(quantity, 10) === 0 ? "cart:item-remove" : "cart:item-quantity-update", {
            detail: { cart: parsedState, context: this.eventContext },
          }),
        );
        if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items", cartData: parsedState, variantId: changedVariantId });
        }
      })
      .catch(() => this.setError(window.cartStrings.error))
      .finally(() => this.disableLoading());
  }

  setError(error) {
    const node = document.getElementById("cart-errors");
    if (node) node.textContent = error;
  }

  updateLiveRegions(line, message) {
    const text = document.getElementById(`Line-item-error-${line}`)?.querySelector(".cart-item-error-text");
    if (text) text.textContent = message || "";
    this.lineItemStatusElement?.setAttribute("aria-hidden", "true");
    const cartStatus = document.getElementById("cart-live-region-text");
    if (cartStatus) {
      cartStatus.setAttribute("aria-hidden", "false");
      setTimeout(() => cartStatus.setAttribute("aria-hidden", "true"), 1000);
    }
  }

  updateSectionContents() {
    const sections = getUniqueSectionNames(this.getSectionsToRender());
    if (!sections.length) return Promise.resolve();
    return fetch(`${window.location.pathname}?sections=${sections.join(",")}&v=${Date.now()}`)
      .then((response) => response.json())
      .then((response) => {
        this.getSectionsToRender().forEach((section) => {
          const root = document.getElementById(section.id);
          if (!root) return;
          let containers = root.querySelectorAll(section.selector);
          if (!containers.length) containers = [root];
          const replacement = this.getSectionInnerHTML(response?.[section.section], section.selector);
          if (replacement === null) return;
          containers.forEach((element) => { if (element) element.innerHTML = replacement; });
        });
      });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    if (!html) return null;
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector)?.innerHTML ?? null;
  }

  enableLoading(line) {
    document.getElementById("main-cart-items")?.classList.add("cart-items-disabled");
    this.querySelectorAll(`#CartItem-${line} .cart-item-loading-overlay`).forEach((overlay) => overlay.classList.remove("hidden"));
    document.activeElement?.blur?.();
    this.lineItemStatusElement?.setAttribute("aria-hidden", "false");
  }

  disableLoading() {
    document.getElementById("main-cart-items")?.classList.remove("cart-items-disabled");
    this.querySelectorAll(".cart-item-loading-overlay").forEach((overlay) => overlay.classList.add("hidden"));
  }
}
if (!customElements.get("cart-items")) customElements.define("cart-items", CartItems);

class MiniCart extends CartItems {
  constructor() {
    super();
    this.toggle = this.querySelector("drawer-toggle");
    this.drawer = this.querySelector("drawer-modal");
  }

  connectedCallback() {
    if (this._bmConnected) return;
    this._bmConnected = true;
    window.miniCart = this;
    document.addEventListener("open-mini-cart", (event) => this.open(event.detail?.opener || null));
    document.addEventListener("close-mini-cart", () => this.close());
    document.addEventListener("toggle-mini-cart", (event) => {
      this.drawer?.isOpen ? this.close() : this.open(event.detail?.opener || null);
    });
  }

  open(opener) {
    this.toggle?.open?.(opener);
    this.dispatchEvent(new CustomEvent("mini-cart:opened", { bubbles: true }));
  }

  close() {
    this.toggle?.close?.();
    this.dispatchEvent(new CustomEvent("mini-cart:closed", { bubbles: true }));
  }

  getSectionsToRender() {
    return [
      { id: "header", section: document.getElementById("header")?.dataset.id, selector: ".head-slot-cart-link" },
      { id: "header-mini-cart-content", section: "header-mini-cart", selector: "#header-mini-cart-content" },
      { id: "header-mini-cart-footer", section: "header-mini-cart", selector: "#header-mini-cart-footer" },
    ].filter(isSectionRenderTargetPresent);
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const root = document.getElementById(section.id);
      if (!root) return;
      let containers = root.querySelectorAll(section.selector);
      if (!containers.length) containers = [root];
      const replacement = this.getSectionInnerHTML(parsedState.sections?.[section.section], section.selector);
      if (replacement === null) return;
      containers.forEach((element) => { if (element) element.innerHTML = replacement; });
    });
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
      (total, input) => total + (parseInt(input.value, 10) || 0),
      0,
    );
  }

  setError(error) {
    const node = document.getElementById("mini-cart-error");
    if (node) node.textContent = error;
  }

  enableLoading() {
    this.querySelector(".mini-cart-wrap")?.classList.add("loading");
  }

  disableLoading() {
    this.querySelector(".mini-cart-wrap")?.classList.remove("loading");
  }

  updateLiveRegions(line, message) {
    const text = document.getElementById(`MiniCart-Line-item-error-${line}`)?.querySelector(".cart-item-error-text");
    if (text) text.textContent = message || "";
  }
}
if (!customElements.get("mini-cart")) customElements.define("mini-cart", MiniCart);

class DrawerModal extends HTMLElement {
  constructor() {
    super();
    this.bodyClass = this.getAttribute("body-class");
    this.isOpen = false;
    this.eventPrefix = this.dataset.eventPrefix;
    this.addEventListener("keyup", (event) => {
      if (event.code?.toUpperCase() === "ESCAPE" && this.isOpen) this.close();
    });
  }

  open(opener) {
    this.isOpen = true;
    this.openedBy = opener;
    if (this.bodyClass) document.body.classList.add(this.bodyClass);
    document.body.classList.add("drawer-open");
    if (this.eventPrefix) document.dispatchEvent(new CustomEvent(`${this.eventPrefix}:open`));
  }

  close() {
    this.isOpen = false;
    if (this.bodyClass) document.body.classList.remove(this.bodyClass);
    document.body.classList.remove("drawer-open");
    removeTrapFocus(this.openedBy || null);
    if (this.eventPrefix) document.dispatchEvent(new CustomEvent(`${this.eventPrefix}:close`));
  }
}
if (!customElements.get("drawer-modal")) customElements.define("drawer-modal", DrawerModal);

class DrawerToggle extends HTMLElement {
  constructor() {
    super();
    this.toggleElement = this.querySelector("a") || this.querySelector("button");
    this.preventOpen = this.getAttribute("prevent-open") === "true";
    if (!this.preventOpen) {
      this.toggleElement?.addEventListener("click", (event) => {
        event.preventDefault();
        this.handleToggle();
      });
    }
  }

  drawer() {
    return document.getElementById(this.getAttribute("for"));
  }

  handleToggle(opener) {
    const drawer = this.drawer();
    if (!drawer) return;
    drawer.isOpen ? this.close() : this.open(opener);
  }

  open(opener) {
    const drawer = this.drawer();
    if (!drawer) return;
    drawer.open(opener || this.toggleElement);
    trapFocus(drawer, drawer.querySelector(".drawer-header") || drawer);
  }

  close() {
    const drawer = this.drawer();
    if (drawer?.isOpen) drawer.close();
  }
}
if (!customElements.get("drawer-toggle")) customElements.define("drawer-toggle", DrawerToggle);

function wrapRichTextMedia(root = document) {
  root.querySelectorAll?.(".rte table").forEach((table) => {
    if (table.closest(".table-wrapper")) return;
    const wrapper = document.createElement("div");
    wrapper.className = "table-wrapper";
    table.parentNode?.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });
  root.querySelectorAll?.(".rte iframe").forEach((iframe) => {
    if (iframe.closest(".video-wrapper")) return;
    const src = iframe.getAttribute("src") || "";
    if (!/youtube|youtu\.be|vimeo/i.test(src)) return;
    const wrapper = document.createElement("div");
    wrapper.className = "video-wrapper";
    iframe.parentNode?.insertBefore(wrapper, iframe);
    wrapper.appendChild(iframe);
  });
}
wrapRichTextMedia();
